import React, { useEffect, useState, createContext, useContext } from 'react';
export interface PriceConfig {
  country: string;
  main: string; // precio principal: "14,99 €"
  compareAt: string; // precio tachado: "130 €"
  bonusValue: string; // valor bonos sticky: "+90 €"
  bonusTotalDisplay: string; // total bonos en BonusSection: "+90€ EN BONOS"
  singlePayment: string; // texto inline en FAQ/Footer: "14,99 €"
  itemSuffix: string; // sufijo para items de la lista: "€", "US$", "MXN"...
  currency: string;
}
const PRICE_TABLE: Record<string, PriceConfig> = {
  US: {
    country: 'US',
    main: '$17 USD',
    compareAt: '$150 USD',
    bonusValue: '+$100 USD',
    bonusTotalDisplay: '+100US$',
    singlePayment: '17 US$',
    itemSuffix: 'US$',
    currency: 'USD'
  },
  ES: {
    country: 'ES',
    main: '14,99 €',
    compareAt: '130 €',
    bonusValue: '+90 €',
    bonusTotalDisplay: '+90€',
    singlePayment: '14,99 €',
    itemSuffix: '€',
    currency: 'EUR'
  },
  IT: {
    country: 'IT',
    main: '14,99 €',
    compareAt: '130 €',
    bonusValue: '+90 €',
    bonusTotalDisplay: '+90€',
    singlePayment: '14,99 €',
    itemSuffix: '€',
    currency: 'EUR'
  },
  MX: {
    country: 'MX',
    main: '309 MXN',
    compareAt: '2.700 MXN',
    bonusValue: '+1.800 MXN',
    bonusTotalDisplay: '+1.800 MXN',
    singlePayment: '309 MXN',
    itemSuffix: 'MXN',
    currency: 'MXN'
  },
  CL: {
    country: 'CL',
    main: '14.900 CLP',
    compareAt: '130.000 CLP',
    bonusValue: '+90.000 CLP',
    bonusTotalDisplay: '+90.000 CLP',
    singlePayment: '14.900 CLP',
    itemSuffix: 'CLP',
    currency: 'CLP'
  },
  CO: {
    country: 'CO',
    main: '62.900 COP',
    compareAt: '550.000 COP',
    bonusValue: '+360.000 COP',
    bonusTotalDisplay: '+360.000 COP',
    singlePayment: '62.900 COP',
    itemSuffix: 'COP',
    currency: 'COP'
  },
  UY: {
    country: 'UY',
    main: '690 UYU',
    compareAt: '6.000 UYU',
    bonusValue: '+4.000 UYU',
    bonusTotalDisplay: '+4.000 UYU',
    singlePayment: '690 UYU',
    itemSuffix: 'UYU',
    currency: 'UYU'
  }
};
function detectCountry(): string {
  const shopify = (window as any).Shopify;
  if (shopify?.country) {
    const c = shopify.country.toUpperCase();
    if (PRICE_TABLE[c]) return c;
  }
  if (shopify?.locale) {
    const locale = shopify.locale.toLowerCase();
    if (locale.includes('mx')) return 'MX';
    if (locale.includes('cl')) return 'CL';
    if (locale.includes('co')) return 'CO';
    if (locale.includes('uy')) return 'UY';
    if (locale.includes('it')) return 'IT';
    if (locale.includes('es')) return 'ES';
  }
  const lang = (navigator.language || navigator.languages && navigator.languages[0] || 'en-US').toLowerCase();
  if (lang === 'es-mx' || lang.startsWith('es-mx')) return 'MX';
  if (lang === 'es-cl' || lang.startsWith('es-cl')) return 'CL';
  if (lang === 'es-co' || lang.startsWith('es-co')) return 'CO';
  if (lang === 'es-uy' || lang.startsWith('es-uy')) return 'UY';
  if (lang.startsWith('it')) return 'IT';
  if (lang.startsWith('es')) return 'ES';
  return 'US';
}
const PriceContext = createContext<PriceConfig>(PRICE_TABLE['US']);
export function PriceProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [priceConfig, setPriceConfig] = useState<PriceConfig>(PRICE_TABLE['US']);
  useEffect(() => {
    const country = detectCountry();
    const config = PRICE_TABLE[country] || PRICE_TABLE['US'];
    setPriceConfig(config);
    if (process.env.NODE_ENV === 'development') {
      console.log(`[PriceByCountry] Detected: ${country} → ${config.main}`);
    }
  }, []);
  return <PriceContext.Provider value={priceConfig} data-id="element-169">
      {children}
    </PriceContext.Provider>;
}
export function usePriceConfig(): PriceConfig {
  return useContext(PriceContext);
}